import { useCallback } from "react";

export type SearchFilter = Record<string, any>;

export interface EntitySearchResult {
  items: any[];
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
  nextPage: number;
  [key: string]: any;
}

export interface SearchParams {
  page?: number;
  limit?: number;
  filter?: SearchFilter;
  includes?: string[];
  sort?: Record<string, any>;
  select?: string | string[] | Record<string, any>;
}

interface UseEntitySearchParams {
  list: (params: any) => Promise<any>;
  key: string;
  includes?: string[];
  filter?: SearchFilter;
  resultKey?: string;
  defaultSort?: Record<string, any>;
}

/**
 * Busca paginada da entidade, no formato que o AutoComplete consome
 *
 * O que chega por argumento soma ao que o hook já tem: os includes são
 * concatenados e os filtros mesclados, com o do argumento por cima.
 *
 * O retorno traz sempre `items` mais os campos de paginação da resposta.
 *
 * @param list - Função de listagem do serviço
 * @param key - Chave dos itens na resposta, ex.: "services"
 * @param includes - Includes fixos do hook
 * @param filter - Filtro corrente do hook
 * @param resultKey - Nome alternativo para os itens no retorno
 * @param defaultSort - Ordenação de partida, que o argumento pode sobrescrever
 * @returns A função de busca
 */
export const useEntitySearch = ({
  list,
  key,
  includes = [],
  filter = {},
  resultKey,
  defaultSort,
}: UseEntitySearchParams) =>
  useCallback(
    async ({
      page = 1,
      limit,
      filter: filterParam = {},
      includes: includesParam = [],
      sort,
      select,
    }: SearchParams) => {
      const data = await list({
        includes: [...(includes || []), ...includesParam],
        page,
        filter: { ...filter, ...filterParam },
        limit,
        sort: defaultSort ? { ...defaultSort, ...sort } : sort,
        select,
      });

      const items = data?.[key] ?? [];

      const result: EntitySearchResult = {
        items,
        page: data?.currentPage,
        limit: data?.limit,
        totalItems: data?.totalItems,
        totalPages: data?.totalPages,
        nextPage: data?.nextPage,
      };
      if (resultKey) result[resultKey] = items;

      return result;
    },
    [list, key, includes, filter, resultKey, defaultSort],
  );
